// @ts-nocheck
import translate from "@insite/client-framework/Translate";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { priceSecondarySpacingStyles, priceStyles, priceVatLabelStyles } from "@store/Themes/common/mixins/price";
import { ProductLineComponentModel } from "@store/Library/Components/ProductLine/ProductLine";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@store/Overrides/Components/SmallHeadingAndText";
import { productLineV2Props } from "@store/Themes/common/props/productLineV2";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { useSettingsCollection } from "@toolkit/Framework/Store/Context/ContextHooks";
import resolveZeroPriceDisplay, {
    getCurrencyAwareZeroPriceDisplay,
} from "@store/Framework/Common/Utilities/resolveZeroPriceDisplay";
import { useCurrentPage } from "@toolkit/Framework/Store/Data/Pages/PageHooks";
import React, { FC } from "react";
import { css } from "styled-components";

export interface ProductLineSubtotalProps {
    productLine?: ProductLineComponentModel;
}

export interface ProductLineSubtotalStyles {
    smallHeadingAndText?: SmallHeadingAndTextStyles;
    vatNote?: TypographyPresentationProps;
    vatPrice?: TypographyPresentationProps;
}

export const productLineSubtotalStyles: ProductLineSubtotalStyles = {
    smallHeadingAndText: mergeProps<ProductLineSubtotalStyles["smallHeadingAndText"]>(
        productLineV2Props.infoSmallHeadingAndText,
        {
            text: {
                css: css`
                    font-weight: ${x => x.theme.design.typography.weight.bold};
                `,
            },
        },
    ),
    vatNote: {
        css: css`
            ${priceVatLabelStyles()}
        `,
    },
    vatPrice: {
        css: css`
            ${priceStyles()}
            ${priceSecondarySpacingStyles()}
            display: block;
            font-weight: ${x => x.theme.design.typography.weight.bold};
        `,
    },
};

const styles = productLineSubtotalStyles;

const ProductLineSubtotal: FC<ProductLineSubtotalProps> = ({ productLine }) => {
    const {
        productSettings: { enableVat, vatPriceDisplay },
    } = useSettingsCollection();
    const pageType = useCurrentPage().type;

    if (!productLine) {
        return null;
    }

    const { extendedUnitNetPriceDisplay, netPriceWithVatDisplay, pricing, taxRate } = productLine;
    const priceWithVat = pricing?.extendedUnitRegularPriceWithVatDisplay || netPriceWithVatDisplay;
    const priceWithoutVat = pricing?.extendedUnitNetPriceDisplay || extendedUnitNetPriceDisplay;

    const showVatPrice = enableVat && vatPriceDisplay !== "DisplayWithoutVat";
    const subtotalDecision = resolveZeroPriceDisplay({
        context: "lineSubtotal",
        pricing,
        pageType,
    });
    const zeroPriceCurrency = productLine.pricing?.additionalResults?.currency;
    const zeroPriceDisplay = getCurrencyAwareZeroPriceDisplay(zeroPriceCurrency);
    const price = subtotalDecision.showDash ? zeroPriceDisplay : showVatPrice ? priceWithVat : priceWithoutVat;
    const priceWithoutVatDisplay = subtotalDecision.showDash ? zeroPriceDisplay : priceWithoutVat;
    const vatRate = pricing?.vatRate ?? taxRate;

    return (
        <SmallHeadingAndText
            extendedStyles={styles.smallHeadingAndText}
            heading={translate("Subtotal")}
            text={
                <>
                    {price}
                    {enableVat && (
                        <>
                            <Typography {...styles.vatNote}>
                                {vatPriceDisplay === "DisplayWithVat" || vatPriceDisplay === "DisplayWithAndWithoutVat"
                                    ? `${translate("Inc. VAT")} (${vatRate}%)`
                                    : translate("Ex. VAT")}
                            </Typography>
                            {vatPriceDisplay === "DisplayWithAndWithoutVat" && (
                                <>
                                    <Typography {...styles.vatPrice}>{priceWithoutVatDisplay}</Typography>
                                    <Typography {...styles.vatNote}>{translate("Ex. VAT")}</Typography>
                                </>
                            )}
                        </>
                    )}
                </>
            }
            useAriaLabel={true}
        />
    );
};

export default ProductLineSubtotal;
